import React, { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import { Gantt, Willow } from '@svar-ui/react-gantt'
import '@svar-ui/react-gantt/all.css'
import { Settings } from 'lucide-react'

import BaseTile from '@/components/Tile/BaseTile'
import TileSettingsModal from '@/components/common/TileSettingsModal'
import { useLocalFilters } from '@/hooks/useLocalFilters'
import { useOcelMetadata } from '@/stores/OcelContext'
import { cn } from '@/lib/utils'
import { transformToGanttData, buildMarkerColorMap, buildColumnDecimalMap, formatAttributeValue } from './ganttTransform'
import { useGanttSettings } from './useGanttSettings'
import GanttSettings from './GanttSettings'

// ISO week number (Mon-based)
const isoWeek = (d) => {
  const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
  tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7))
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1))
  return Math.ceil((((tmp - yearStart) / 86400000) + 1) / 7)
}

const ZOOM_LEVELS = [
  {
    id: 'year',
    label: 'Jahr',
    scales: [
      { unit: 'year',    step: 1, format: (d) => `${d.getFullYear()}` },
      { unit: 'quarter', step: 1, format: (d) => `Q${Math.floor(d.getMonth() / 3) + 1}` },
      { unit: 'month',   step: 1, format: (d) => d.toLocaleDateString('de-DE', { month: 'short' }) },
    ],
  },
  {
    id: 'quarter',
    label: 'Quartal',
    scales: [
      { unit: 'quarter', step: 1, format: (d) => `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}` },
      { unit: 'month',   step: 1, format: (d) => d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }) },
      { unit: 'week',    step: 1, format: (d) => `KW ${isoWeek(d)}` },
    ],
  },
  {
    id: 'month',
    label: 'Monat',
    scales: [
      { unit: 'month', step: 1, format: (d) => d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }) },
      { unit: 'day',   step: 1, format: (d) => d.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric' }) },
    ],
  },
  {
    id: 'week',
    label: 'Woche',
    cellWidth: 100,
    scales: [
      { unit: 'week', step: 1, format: (d) => `KW ${isoWeek(d)} ${d.getFullYear()}` },
      { unit: 'day',  step: 1, format: (d) => d.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' }) },
    ],
  },
  {
    id: 'day',
    label: 'Tag',
    scales: [
      { unit: 'day',  step: 1, format: (d) => d.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) },
      { unit: 'hour', step: 1, format: (d) => `${d.getHours()}:00` },
    ],
  },
]

const SVAR_ROW_H    = 38
const SVAR_SCALE_H  = 36
const SVAR_SCROLL_H = 38
const MAX_GANTT_ROWS = 20

const BASE_COLUMNS = [
  { id: 'text',  header: 'Task Name',   width: 200, resize: true, cell: TaskNameCell },
  { id: 'start', header: 'Start Datum', width: 110, align: 'center' },
]

const SYSTEM_FONT = 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif'

const svarScrollFix = `
  .wx-willow,
  .wx-willow-theme { height: 100%; --wx-font-family: ${SYSTEM_FONT}; }
  .wx-pseudo-rows { padding-bottom: 24px; }
  .wx-table       { padding-bottom: 24px; }

  .wx-table .wx-grid .wx-header .wx-cell { background: hsl(var(--muted)) !important; color: hsl(var(--muted-foreground)) !important; font-family: ${SYSTEM_FONT} !important; font-size: 0.75rem !important; font-weight: 500 !important; text-transform: uppercase; letter-spacing: 0.05em; }
  .wx-table .wx-grid .wx-body   .wx-cell { font-family: ${SYSTEM_FONT} !important; font-size: 0.875rem !important; color: hsl(var(--foreground)); }

  .wx-scale > .wx-row:nth-child(1) .wx-cell { background: hsl(var(--muted)); color: hsl(var(--foreground)); font-family: ${SYSTEM_FONT}; font-weight: 600; font-size: 0.8rem; }
  .wx-scale > .wx-row:nth-child(2) .wx-cell { background: hsl(var(--background)); color: hsl(var(--muted-foreground)); font-family: ${SYSTEM_FONT}; font-weight: 500; font-size: 0.76rem; }
  .wx-scale > .wx-row:nth-child(3) .wx-cell { background: hsl(var(--background)); color: hsl(var(--muted-foreground)); font-family: ${SYSTEM_FONT}; font-weight: 400; font-size: 0.72rem; border-bottom: 1px solid hsl(var(--border)); }
  .wx-bar .wx-content { overflow: visible !important; }
  .wx-scale { position: relative; z-index: 20 !important; background: hsl(var(--background)); }
`

const GanttTooltipCtx = createContext(null)

const fmtDate = (d) => {
  if (!(d instanceof Date)) return '—'
  const p = (n) => String(n).padStart(2, '0')
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
}

const fmtDuration = (h) => {
  if (h == null) return '—'
  const d = Math.floor(h / 24)
  const rem = Math.round(h % 24)
  if (d === 0) return `${rem}h`
  if (rem === 0) return `${d}T`
  return `${d}T ${rem}h`
}

function TooltipRow({ label, value, note, noteColor }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
      <span style={{ fontSize: '0.7rem', color: '#64748b', flexShrink: 0 }}>{label}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        {note && (
          <span style={{ fontSize: '0.62rem', color: noteColor ?? '#f59e0b', fontStyle: 'italic' }}>{note}</span>
        )}
        <span style={{ fontSize: '0.72rem', fontWeight: 500, color: '#1e293b' }}>{value}</span>
      </span>
    </div>
  )
}

/** Formats a configured tooltip attribute value; timestamp-typed attributes
 * render with the same fmtDate used elsewhere in the tooltip/app, everything
 * else falls back to a plain string. Missing values render as an empty
 * string — the row/label is still shown (per spec: "Attribut wird trotzdem
 * angezeigt"). */
function formatTooltipValue(attr, value, attrTypes, decimalMap, unitMap, isNumericMap) {
  if (value == null) return ''
  if (attrTypes?.[attr] === 'timestamp') {
    const d = new Date(value)
    if (!isNaN(d.getTime())) return fmtDate(d)
  }
  return formatAttributeValue(value, {
    hasDecimals: decimalMap?.[attr],
    unit: unitMap?.[attr],
    isNumericAttr: isNumericMap?.[attr],
  })
}

/** Unified tooltip. Summary rows keep the original Ist-/Soll-Zeitraum
 * display (a group of objects has no single "object" to show configurable
 * attributes for). Task rows show the new configurable header + attribute
 * list instead. */
function GanttTooltip({ task, x, y, summaryLabel, attrTypes, decimalMap, unitMap, isNumericMap }) {
  const { t } = useTranslation('tiles')
  const OFFSET = 16
  const left = Math.min(x + OFFSET, window.innerWidth - 300)
  const top  = Math.min(y + OFFSET, window.innerHeight - 220)

  const isSummary = task.type === 'summary'
  // Use the bar's own colour; fall back to SVAR Willow theme defaults
  const accentColor = task.color ?? (isSummary ? '#00ba94' : '#3983eb')

  const hasPlanned = isSummary && (task.plannedStart instanceof Date || task.plannedEnd instanceof Date)
  const entries = isSummary ? [] : Object.entries(task.tooltipValues || {})

  return (
    <div style={{
      position: 'fixed', left, top, zIndex: 9999,
      width: 284, background: '#ffffff',
      border: '1px solid #e2e8f0', borderRadius: 10,
      boxShadow: '0 8px 24px rgba(0,0,0,0.13), 0 2px 6px rgba(0,0,0,0.07)',
      overflow: 'hidden', pointerEvents: 'none',
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, sans-serif',
    }}>
      {/* coloured top bar */}
      <div style={{ height: 3, background: accentColor }} />

      {/* header */}
      <div style={{ padding: '9px 14px 8px', borderBottom: (isSummary || entries.length > 0) ? '1px solid #f1f5f9' : 'none' }}>
        {isSummary && (
          <span style={{
            display: 'inline-block', marginBottom: 5,
            fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.09em',
            textTransform: 'uppercase', color: '#fff',
            background: accentColor, padding: '1px 7px', borderRadius: 4,
          }}>
            {summaryLabel || t('gantt.tooltip.summary')}
          </span>
        )}
        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#0f172a', lineHeight: 1.35, wordBreak: 'break-word' }}>
          {task.text}
        </div>
      </div>

      {isSummary ? (
        <>
          {/* IST section */}
          <div style={{ padding: '8px 14px', borderBottom: hasPlanned ? '1px solid #f1f5f9' : 'none' }}>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: 6 }}>
              {t('gantt.tooltip.actualPeriod')}
            </div>
            <TooltipRow
              label={t('gantt.tooltip.start')}
              value={fmtDate(task.start)}
              note={task.isStartFallback ? '◁ geschätzt' : null}
            />
            <TooltipRow
              label={t('gantt.tooltip.end')}
              value={fmtDate(task.end)}
              note={task.isEndToday ? 'heute' : task.isEndLastFallback ? '▷ letztes Ereignis' : null}
            />
            <TooltipRow label={t('gantt.tooltip.duration')} value={fmtDuration(task.durationH)} />
          </div>

          {/* SOLL section */}
          {hasPlanned && (
            <div style={{ padding: '8px 14px', background: '#f8fafc' }}>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: 6 }}>
                {t('gantt.tooltip.plannedPeriod')}
              </div>
              {task.plannedStart instanceof Date && (
                <TooltipRow label={t('gantt.tooltip.start')} value={fmtDate(task.plannedStart)} />
              )}
              {task.plannedEnd instanceof Date && (
                <TooltipRow label={t('gantt.tooltip.end')} value={fmtDate(task.plannedEnd)} />
              )}
            </div>
          )}
        </>
      ) : (
        entries.length > 0 && (
          <div style={{ padding: '8px 14px' }}>
            {entries.map(([attr, value]) => (
              <TooltipRow key={attr} label={attr} value={formatTooltipValue(attr, value, attrTypes, decimalMap, unitMap, isNumericMap)} />
            ))}
          </div>
        )
      )}
    </div>
  )
}

/** Bar hover: summary bars always show their (unconfigurable) Ist-/Soll tooltip;
 * task bars only show the new configurable tooltip once at least one attribute
 * is selected. */
function shouldShowBarTooltip(row) {
  if (row?.type === 'summary') return true
  return row?.type === 'task' && Object.keys(row?.tooltipValues || {}).length > 0
}

/** Name-cell hover only ever applies to task rows (summary rows never had a
 * name-cell tooltip trigger, old or new), and only once attributes are configured. */
function hasTaskTooltip(row) {
  return row?.type === 'task' && Object.keys(row?.tooltipValues || {}).length > 0
}

function GanttTaskMarkers({ data }) {
  const ctx = useContext(GanttTooltipCtx)
  const duration = data.end - data.start
  if (duration <= 0) return null
  const pct = (date) => ((date - data.start) / duration) * 100
  const showsTooltip = shouldShowBarTooltip(data)
  return (
    <div
      style={{ position: 'relative', width: '100%', height: '100%' }}
      onMouseEnter={showsTooltip ? (e) => ctx?.showTooltip(data, e) : undefined}
      onMouseMove={showsTooltip ? (e) => ctx?.moveTooltip(e) : undefined}
      onMouseLeave={showsTooltip ? () => ctx?.hideTooltip() : undefined}
    >
      <div style={{
        position: 'absolute', left: 4, right: 4, top: 0, bottom: 0,
        display: 'flex', alignItems: 'center',
        overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
        fontSize: '0.72rem', fontWeight: 500,
        color: 'rgba(255,255,255,0.95)', textShadow: '0 1px 2px rgba(0,0,0,0.45)',
        userSelect: 'none', zIndex: 1, pointerEvents: 'none',
      }}>
        {data.text}
      </div>
      {data.plannedStart instanceof Date && (
        <div
          style={{ position: 'absolute', left: `${pct(data.plannedStart)}%`, top: '10%', height: '80%', width: '2px', background: '#0f172a', zIndex: 5, transform: 'translateX(0)', willChange: 'transform' }}
        />
      )}
      {data.plannedEnd instanceof Date && (
        <div
          style={{ position: 'absolute', left: `${pct(data.plannedEnd)}%`, top: '10%', height: '80%', width: '2px', background: '#b91c1c', zIndex: 5, transform: 'translateX(0)', willChange: 'transform' }}
        />
      )}
      {data.type === 'task' && Array.isArray(data.markerEvents) && data.markerEvents.map((ev, i) => (
        <div
          key={`${ev.activity}-${ev.timestamp?.getTime()}-${i}`}
          title={`${ev.activity}\n${fmtDate(ev.timestamp)}`}
          style={{
            position: 'absolute', left: `${pct(ev.timestamp)}%`, top: '50%',
            width: 9, height: 9, borderRadius: '50%',
            background: ev.color || '#7c3aed', border: '1.5px solid #fff',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.25)',
            transform: 'translate(-50%, -50%)', zIndex: 6, pointerEvents: 'auto',
          }}
        />
      ))}
    </div>
  )
}

/** Custom cell for the grid's primary "text" column — SVAR renders this
 * inside its own tree-indent/expand-toggle wrapper, so hooking in here only
 * replaces the label content, not the tree behaviour. Triggers the same
 * unified tooltip as the bar (GanttTaskMarkers), scoped to task rows only. */
function TaskNameCell({ row }) {
  const ctx = useContext(GanttTooltipCtx)
  const showsTooltip = hasTaskTooltip(row)
  return (
    <span
      onMouseEnter={showsTooltip ? (e) => ctx?.showTooltip(row, e) : undefined}
      onMouseMove={showsTooltip ? (e) => ctx?.moveTooltip(e) : undefined}
      onMouseLeave={showsTooltip ? () => ctx?.hideTooltip() : undefined}
    >
      {row.text}
    </span>
  )
}

const GanttView = memo(function GanttView({ tasks, columns, scales, cellWidth, handleInit, showTooltip, moveTooltip, hideTooltip }) {
  return (
    <GanttTooltipCtx.Provider value={{ showTooltip, moveTooltip, hideTooltip }}>
      <Willow>
        <Gantt
          tasks={tasks}
          links={[]}
          scales={scales}
          columns={columns}
          init={handleInit}
          readonly={true}
          zoom={true}
          durationUnit="hour"
          {...(cellWidth != null ? { cellWidth } : {})}
          taskTemplate={GanttTaskMarkers}
        />
      </Willow>
    </GanttTooltipCtx.Provider>
  )
})

export default function GanttChart({ activeFile, onRefresh, onGetHeaderActions, onTitleChange, onRegisterSettingsGetter, initialSettings }) {
  const metadata = useOcelMetadata(activeFile)

  // Strip the non-Gantt-setting keys that ride along in the saved payload
  // (componentTitle/zoomId/localFilters are restored into their own state below).
  const { componentTitle: savedTitle, zoomId: savedZoomId, localFilters: _savedLocalFilters, ...savedGanttSettings } = initialSettings || {}

  // Seeded synchronously from initialSettings (not via a mount effect) so
  // settings.objectType is already correct on the very first render — avoids
  // a race with the "auto-initialize objectType" effect below, which would
  // otherwise stomp the restored objectType whenever metadata is already
  // cached at mount time (e.g. tile remounted via configEpoch bump rather
  // than a full page reload).
  const [settings, , updateSettings] = useGanttSettings(initialSettings ? savedGanttSettings : undefined)

  const [tableData, setTableData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [draftSettings, setDraftSettings] = useState(null)
  const [componentTitle, setComponentTitle] = useState(savedTitle ?? 'Gantt Chart')
  const [draftTitle, setDraftTitle] = useState(savedTitle ?? 'Gantt Chart')

  const [zoomId, setZoomId] = useState(
    savedZoomId && ZOOM_LEVELS.some(z => z.id === savedZoomId) ? savedZoomId : 'month'
  )

  const [tooltip, setTooltip] = useState({ visible: false, task: null, x: 0, y: 0 })
  const showTooltip = useCallback((task, e) => setTooltip({ visible: true, task, x: e.clientX, y: e.clientY }), [])
  const moveTooltip = useCallback((e) => setTooltip(p => ({ ...p, x: e.clientX, y: e.clientY })), [])
  const hideTooltip = useCallback(() => setTooltip(p => ({ ...p, visible: false })), [])

  const {
    api,
    currentFilters,
    localFilters,
    updateLocalFilter,
    startDraftMode,
    applyDraftFilters,
    cancelDraftFilters,
  } = useLocalFilters(activeFile, initialSettings?.localFilters ?? null)

  const svarApiRef   = useRef(null)
  const ganttWrapRef = useRef(null)

  // Stable refs for parent callbacks
  const onTitleChangeRef = useRef(onTitleChange)
  useEffect(() => { onTitleChangeRef.current = onTitleChange })
  const onRefreshRef = useRef(onRefresh)
  useEffect(() => { onRefreshRef.current = onRefresh })
  const onGetHeaderActionsRef = useRef(onGetHeaderActions)
  useEffect(() => { onGetHeaderActionsRef.current = onGetHeaderActions })

  // Derived schema from metadata
  const objectTypes = useMemo(
    () => (metadata?.object_types ? Object.keys(metadata.object_types) : []),
    [metadata],
  )
  const activities = useMemo(
    () => (metadata?.activity_types ? Object.keys(metadata.activity_types) : []),
    [metadata],
  )
  const attributes = useMemo(() => {
    const names = new Set([
      ...(metadata?.event_attribute_names ?? []),
      ...(metadata?.object_attribute_names ?? []),
    ])
    return [...names]
  }, [metadata])
  const attributesByType = useMemo(() => {
    const map = {}
    for (const ot of metadata?.object_types_schema || []) {
      map[ot.name] = (ot.attributes || []).map(a => a.name)
    }
    return map
  }, [metadata])
  // Attribute-name -> declared OCEL type ('timestamp', 'string', ...) for the
  // currently configured object type, used to format tooltip attribute values
  // (e.g. rendering a timestamp attribute with the same fmtDate as other dates).
  const tooltipAttrTypes = useMemo(() => {
    const map = {}
    const ot = (metadata?.object_types_schema || []).find(t => t.name === settings.objectType)
    for (const a of ot?.attributes || []) {
      map[a.name] = (a.type || '').toLowerCase()
    }
    return map
  }, [metadata, settings.objectType])

  // Attribute-name -> unit ('EUR', 'd', ...) declared in the OCEL schema, global
  // across event and object attributes (not scoped to settings.objectType, since
  // extra-column/tooltip attribute pickers can fall back to global attribute names).
  // Mirrors TracePivotTable.jsx's attributeUnitMap exactly.
  const attributeUnitMap = useMemo(() => {
    const map = {}
    for (const et of (metadata?.event_types ?? [])) {
      for (const attr of (et.attributes ?? [])) {
        if (attr.unit) map[attr.name] = attr.unit
      }
    }
    for (const ot of (metadata?.object_types_schema ?? [])) {
      for (const attr of (ot.attributes ?? [])) {
        if (attr.unit) map[attr.name] = attr.unit
      }
    }
    return map
  }, [metadata])

  // Attribute-name -> true when the OCEL schema declares this attribute as a
  // numeric type. Global across event and object attributes, mirroring
  // attributeUnitMap exactly. Gates numeric-string coercion in
  // ganttTransform.js so a genuinely-string attribute that happens to
  // contain digit-only values (e.g. a postal code) is never misread as
  // numeric — some OCEL exports serialize non-integer float attribute
  // values as strings (e.g. "546.3992"), which this map lets us still
  // format correctly (unit + decimal trimming) without guessing from the
  // value's own shape.
  const attributeIsNumericMap = useMemo(() => {
    const numericTypes = new Set(['float', 'int', 'integer', 'number', 'double'])
    const map = {}
    for (const et of (metadata?.event_types ?? [])) {
      for (const attr of (et.attributes ?? [])) {
        if (numericTypes.has((attr.type || '').toLowerCase())) map[attr.name] = true
      }
    }
    for (const ot of (metadata?.object_types_schema ?? [])) {
      for (const attr of (ot.attributes ?? [])) {
        if (numericTypes.has((attr.type || '').toLowerCase())) map[attr.name] = true
      }
    }
    return map
  }, [metadata])

  // Register settings getter with parent so it can be collected on Save
  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({ ...settings, componentTitle, localFilters, zoomId }));
  }, [settings, componentTitle, localFilters, zoomId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-initialize objectType once metadata arrives
  useEffect(() => {
    if (objectTypes.length > 0 && !settings.objectType) {
      updateSettings({ objectType: objectTypes[0] })
    }
  }, [objectTypes, settings.objectType, updateSettings])

  // Only the most recently started fetch may commit its result — a slower,
  // older request (e.g. superseded by a filter change) must not overwrite a
  // newer one.
  const fetchSeqRef = useRef(0)
  const fetchData = useCallback(async () => {
    if (!activeFile || !settings.objectType) return
    const seq = ++fetchSeqRef.current
    setLoading(true)
    setError(null)
    try {
      const result = await api.post('/traces/gantt-object-summary', {
        object_type:            settings.objectType,
        summary_attribute:      settings.summaryAttribute,
        task_label_attribute:   settings.taskLabelAttribute,
        task_start_activities:  settings.taskStartActivity,
        task_end_activities:    settings.taskEndActivity,
        task_planned_start:     settings.taskPlannedStart,
        task_planned_end:       settings.taskPlannedEnd,
        summary_planned_start:  settings.summaryPlannedStart,
        summary_planned_end:    settings.summaryPlannedEnd,
        summary_extra_attributes: settings.summaryExtraColumns,
        task_extra_attributes:    settings.taskExtraColumns,
        task_tooltip_attributes:  settings.taskTooltipAttributes,
        marker_activities:        settings.markerActivities,
      })
      if (seq !== fetchSeqRef.current) return
      setTableData(result)
    } catch (err) {
      if (seq !== fetchSeqRef.current) return
      setError(err?.message || 'Failed to load Gantt data')
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false)
    }
  }, [
    api,
    activeFile,
    settings.objectType,
    settings.summaryAttribute,
    settings.taskLabelAttribute,
    settings.taskStartActivity,
    settings.taskEndActivity,
    settings.taskPlannedStart,
    settings.taskPlannedEnd,
    settings.summaryPlannedStart,
    settings.summaryPlannedEnd,
    settings.summaryExtraColumns,
    settings.taskExtraColumns,
    settings.taskTooltipAttributes,
    settings.markerActivities,
  ])

  useEffect(() => {
    if (activeFile) fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeFile,
    settings.objectType,
    settings.summaryAttribute,
    settings.taskLabelAttribute,
    settings.taskStartActivity,
    settings.taskEndActivity,
    settings.taskPlannedStart,
    settings.taskPlannedEnd,
    settings.summaryPlannedStart,
    settings.summaryPlannedEnd,
    settings.summaryExtraColumns,
    settings.taskExtraColumns,
    settings.taskTooltipAttributes,
    settings.markerActivities,
    currentFilters,
  ])

  // Propagate title changes
  useEffect(() => {
    onTitleChangeRef.current?.(componentTitle)
  }, [componentTitle])

  // Register refresh callback
  useEffect(() => {
    onRefreshRef.current?.(fetchData)
  }, [fetchData])

  // Register header actions
  useEffect(() => {
    onGetHeaderActionsRef.current?.(
      <button
        onClick={handleOpenSettings}
        title="Settings"
        className="rounded p-1 hover:bg-muted transition-colors"
      >
        <Settings className="h-4 w-4" />
      </button>,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData])

  // Settings modal handlers
  const handleOpenSettings = () => {
    setDraftSettings({ ...settings })
    setDraftTitle(componentTitle)
    startDraftMode()
    setIsSettingsOpen(true)
  }

  const handleSettingsOpenChange = (val) => {
    setIsSettingsOpen(val)
    if (!val) cancelDraftFilters()
  }

  const handleApplySettings = () => {
    updateSettings(draftSettings)
    setComponentTitle(draftTitle)
    applyDraftFilters()
  }

  // Gantt transform
  const rawData = tableData?.rows ?? []
  const isDays = settings.durationUnit === 'd'

  const rawTasks = useMemo(
    () => transformToGanttData(rawData, settings, 'object_id', new Date()),
    // rawData reference changes on each fetch; settings is the main dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableData, settings],
  )

  function getTaskColor(t) {
    if (t.isSummaryFallback) return '#fb923c'
    if (t.isStartFallback)   return '#9ca3af'
    if (t.isEndToday)        return t.type === 'summary' ? '#4ade80' : '#60a5fa'
    if (t.isEndFallback)     return '#f59e0b'
    return undefined
  }

  const hasStartSymbol = rawTasks.some(t => t.isStartFallback   && t.type === 'task')
  const hasEndSymbol   = rawTasks.some(t => t.isEndLastFallback && t.type === 'task')

  const markerColorMap = useMemo(
    () => buildMarkerColorMap(settings.markerActivities),
    [settings.markerActivities],
  )

  const tasks = useMemo(() => rawTasks.map(t => {
    const color = getTaskColor(t)
    const label = [
      t.isStartFallback                    ? '◁' : '',
      t.isSummaryFallback ? `⚠ ${t.text}` : t.text,
      t.isEndLastFallback                  ? '▷' : '',
    ].filter(Boolean).join(' ')
    return {
      ...t,
      text:            label,
      durationDisplay: isDays ? +(t.durationH / 24).toFixed(1) : t.durationH,
      ...(color != null ? { color } : {}),
    }
  }), [rawTasks, isDays])

  // Union of summary-level then task-level extra columns (summary-first, deduped) —
  // SVAR renders summary/task rows in one shared tree-grid, so there is only one
  // column set; a column is simply blank on rows where that attribute wasn't
  // configured for that row's level (see GanttSettings + ganttTransform).
  const extraColumnAttrs = useMemo(() => {
    const summaryAttrs = settings.summaryExtraColumns || []
    const taskAttrs    = settings.taskExtraColumns || []
    const seen = new Set(summaryAttrs)
    return [...summaryAttrs, ...taskAttrs.filter(a => !seen.has(a))]
  }, [settings.summaryExtraColumns, settings.taskExtraColumns])

  // Column-wide decimal relevance, computed once per data/settings change (not
  // per cell) — a column shows no decimals only when every value in it is a
  // whole number; otherwise all its values keep their decimals.
  const columnDecimalMap = useMemo(
    () => buildColumnDecimalMap(tasks, extraColumnAttrs, (row, attr) => row.extraValues?.[attr], attributeIsNumericMap),
    [tasks, extraColumnAttrs, attributeIsNumericMap],
  )

  // Same check for the tooltip's separately-configurable attribute list.
  const tooltipDecimalMap = useMemo(
    () => buildColumnDecimalMap(tasks, settings.taskTooltipAttributes || [], (row, attr) => row.tooltipValues?.[attr], attributeIsNumericMap),
    [tasks, settings.taskTooltipAttributes, attributeIsNumericMap],
  )

  const columns = useMemo(() => [
    BASE_COLUMNS[0],
    ...extraColumnAttrs.map((attr, i) => ({
      id: `extra__${i}`,
      header: attr,
      width: 140,
      resize: true,
      getter: (row) => formatAttributeValue(row.extraValues?.[attr], {
        hasDecimals: columnDecimalMap[attr],
        unit: attributeUnitMap[attr],
        isNumericAttr: attributeIsNumericMap[attr],
      }),
    })),
    BASE_COLUMNS[1],
    { id: 'durationDisplay', header: isDays ? 'Dauer (d)' : 'Dauer (h)', width: 90, align: 'center' },
  ], [isDays, extraColumnAttrs, columnDecimalMap, attributeUnitMap, attributeIsNumericMap])

  const sortedTasks = useMemo(() => {
    const byText = (a, b) => (a.text?.toLowerCase() ?? '') < (b.text?.toLowerCase() ?? '') ? -1 : 1
    const summaries = tasks.filter(t => t.type === 'summary').slice().sort(byText)
    const childrenMap = new Map()
    for (const t of tasks) {
      if (t.type !== 'summary') {
        if (!childrenMap.has(t.parent)) childrenMap.set(t.parent, [])
        childrenMap.get(t.parent).push(t)
      }
    }
    const result = []
    for (const s of summaries) {
      result.push(s)
      result.push(...(childrenMap.get(s.id) ?? []).slice().sort(byText))
    }
    return result
  }, [tasks])

  const handleGanttInit = useCallback((api) => {
    api.exec('sort-tasks', { key: 'text', order: 'asc' })
    svarApiRef.current = api
  }, [])

  useEffect(() => {
    const wrap = ganttWrapRef.current
    if (!wrap) return

    let initialized = false
    const initTimer = setTimeout(() => { initialized = true }, 400)
    let prevH = 0

    const observer = new ResizeObserver((entries) => {
      if (!initialized) return
      for (const entry of entries) {
        const h = entry.contentRect.height
        if (h < prevH && svarApiRef.current) {
          setTimeout(() => svarApiRef.current.exec('scroll-chart', { top: 0 }), 50)
        }
        prevH = h
      }
    })

    const pseudoRows = wrap.querySelector('.wx-pseudo-rows')
    if (pseudoRows) observer.observe(pseudoRows)

    return () => {
      clearTimeout(initTimer)
      observer.disconnect()
    }
  }, [sortedTasks])

  const hasPlannedConfig = !!(
    settings.summaryPlannedStart || settings.summaryPlannedEnd ||
    settings.taskPlannedStart    || settings.taskPlannedEnd
  )

  const activeZoom = ZOOM_LEVELS.find(z => z.id === zoomId)

  // Always render at the max row count — sizing to the actual (potentially
  // expanded) row count is unreliable since SVAR manages tree expand/collapse
  // internally and we never see that state. A fixed height means rows beyond
  // MAX_GANTT_ROWS are reached via SVAR's own internal scrolling instead of
  // being clipped by our container.
  const ganttHeight = useMemo(() => {
    const headerH = (activeZoom?.scales?.length ?? 2) * SVAR_SCALE_H
    return MAX_GANTT_ROWS * SVAR_ROW_H + headerH + SVAR_SCROLL_H
  }, [activeZoom])

  return (
    <>
      <BaseTile
        loading={loading}
        error={error}
        placeholder={!tableData ? 'Einstellungen konfigurieren, um Daten zu laden' : undefined}
        padding="none"
      >
        {tableData && sortedTasks.length > 0 && (
          <div className="flex flex-col">
            <style>{svarScrollFix}</style>

            {/* Zoom toolbar */}
            <div className="flex items-center gap-1 px-3 py-1.5 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <span className="text-xs text-gray-500 mr-1">Zoom:</span>
              {ZOOM_LEVELS.map(level => (
                <button
                  key={level.id}
                  onClick={() => setZoomId(level.id)}
                  className={cn(
                    'px-2.5 py-1 text-xs border rounded transition-colors cursor-pointer',
                    level.id === zoomId
                      ? 'bg-primary-500 border-primary-500 text-white font-semibold'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50',
                  )}
                >
                  {level.label}
                </button>
              ))}

              {(hasStartSymbol || hasEndSymbol) && (
                <div className="ml-auto flex items-center gap-3">
                  {hasStartSymbol && (
                    <span className="text-xs text-gray-500">
                      <span className="font-semibold">◁</span> Startdatum geschätzt
                    </span>
                  )}
                  {hasEndSymbol && (
                    <span className="text-xs text-amber-800">
                      <span className="font-semibold">▷</span> Enddatum = letztes Ereignis
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Selection info bar */}
            {(settings.summaryAttribute || settings.taskLabelAttribute || settings.taskStartActivity?.length > 0 || settings.taskEndActivity?.length > 0 || settings.taskPlannedStart || settings.taskPlannedEnd || settings.summaryPlannedStart || settings.summaryPlannedEnd || settings.markerActivities?.length > 0) && (
              <div className="flex items-center gap-x-4 gap-y-1 flex-wrap px-4 py-2 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 flex-shrink-0">
                {settings.summaryAttribute && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700">Summary:</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">{settings.summaryAttribute}</span>
                  </div>
                )}
                {settings.summaryAttribute && settings.taskLabelAttribute && (
                  <div className="h-4 w-px bg-gray-300" />
                )}
                {settings.taskLabelAttribute && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700">Task Label:</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">{settings.taskLabelAttribute}</span>
                  </div>
                )}
                {settings.taskStartActivity?.length > 0 && (
                  <>
                    <div className="h-4 w-px bg-gray-300" />
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-700">Start:</span>
                      {settings.taskStartActivity.map(a => (
                        <span key={a} className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm font-medium">{a}</span>
                      ))}
                    </div>
                  </>
                )}
                {settings.taskEndActivity?.length > 0 && (
                  <>
                    <div className="h-4 w-px bg-gray-300" />
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-700">End:</span>
                      {settings.taskEndActivity.map(a => (
                        <span key={a} className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-sm font-medium">{a}</span>
                      ))}
                    </div>
                  </>
                )}
                {settings.markerActivities?.length > 0 && (
                  <>
                    <div className="h-4 w-px bg-gray-300" />
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-700">Marker:</span>
                      {settings.markerActivities.map(a => (
                        <span
                          key={a}
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-sm font-medium"
                          style={{ background: `${markerColorMap[a]}1a`, color: markerColorMap[a] }}
                        >
                          <span className="inline-block h-2 w-2 rounded-full flex-shrink-0" style={{ background: markerColorMap[a] }} />
                          {a}
                        </span>
                      ))}
                    </div>
                  </>
                )}
                {(settings.taskPlannedStart || settings.summaryPlannedStart) && (
                  <>
                    <div className="h-4 w-px bg-gray-300" />
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-700">Plan Start:</span>
                      {settings.taskPlannedStart && (
                        <span className="px-2 py-1 bg-slate-100 text-slate-800 rounded text-sm font-medium">{settings.taskPlannedStart}</span>
                      )}
                      {settings.summaryPlannedStart && settings.summaryPlannedStart !== settings.taskPlannedStart && (
                        <span className="px-2 py-1 bg-slate-100 text-slate-800 rounded text-sm font-medium">{settings.summaryPlannedStart}</span>
                      )}
                    </div>
                  </>
                )}
                {(settings.taskPlannedEnd || settings.summaryPlannedEnd) && (
                  <>
                    <div className="h-4 w-px bg-gray-300" />
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-700">Plan Ende:</span>
                      {settings.taskPlannedEnd && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-sm font-medium">{settings.taskPlannedEnd}</span>
                      )}
                      {settings.summaryPlannedEnd && settings.summaryPlannedEnd !== settings.taskPlannedEnd && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-sm font-medium">{settings.summaryPlannedEnd}</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Gantt chart */}
            <div ref={ganttWrapRef} className="overflow-hidden flex-shrink-0" style={{ height: `${ganttHeight}px` }}>
              <GanttView
                tasks={sortedTasks}
                columns={columns}
                scales={activeZoom.scales}
                cellWidth={activeZoom.cellWidth ?? null}
                handleInit={handleGanttInit}
                showTooltip={showTooltip}
                moveTooltip={moveTooltip}
                hideTooltip={hideTooltip}
              />
            </div>
          </div>
        )}

        {tableData && sortedTasks.length === 0 && !loading && (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground p-6">
            Keine Daten entsprechen den aktuellen Einstellungen und Filtern.
          </div>
        )}
      </BaseTile>

      <TileSettingsModal
        open={isSettingsOpen}
        onOpenChange={handleSettingsOpenChange}
        title="Gantt Chart konfigurieren"
        getInitialValues={() => ({ settings, componentTitle })}
        onApply={handleApplySettings}
      >
        {draftSettings && (
          <GanttSettings
            activeFile={activeFile}
            settings={draftSettings}
            onUpdate={(k, v) => setDraftSettings(p => ({ ...p, [k]: v }))}
            onBatchUpdate={(u) => setDraftSettings(p => ({ ...p, ...u }))}
            objectTypes={objectTypes}
            attributes={attributes}
            attributesByType={attributesByType}
            activities={activities}
            localFilters={localFilters}
            updateLocalFilter={updateLocalFilter}
          />
        )}
      </TileSettingsModal>

      {tooltip.visible && tooltip.task && createPortal(
        <GanttTooltip
          task={tooltip.task}
          x={tooltip.x}
          y={tooltip.y}
          summaryLabel={settings.summaryAttribute}
          attrTypes={tooltipAttrTypes}
          decimalMap={tooltipDecimalMap}
          unitMap={attributeUnitMap}
          isNumericMap={attributeIsNumericMap}
        />,
        document.body,
      )}
    </>
  )
}
